import React, { useState } from 'react';
import { Button } from '../common/Button';
import { useToast } from '../../hooks/useToast';
import { useMoveBackToProductionMutation } from '../../store/slices/apiSlice';
import { Factory, AlertTriangle, RotateCcw } from 'lucide-react';

interface MoveBackToProductionButtonProps {
  jobId: string;
  jobNumber: string;
  onSuccess?: () => void;
  disabled?: boolean;
}

export const MoveBackToProductionButton: React.FC<MoveBackToProductionButtonProps> = ({
  jobId,
  jobNumber,
  disabled = false,
  onSuccess
}) => {
  const toast = useToast();
  const [moveBackToProduction, { isLoading }] = useMoveBackToProductionMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [notes, setNotes] = useState('');

  const handleConfirm = async () => {
    try {
      await moveBackToProduction({
        jobId,
        notes: notes.trim() || undefined
      }).unwrap();
      
      toast.success('Success', `Job ${jobNumber} moved back to New Orders`);
      setShowConfirm(false);
      setNotes('');
      onSuccess?.();
    } catch (error: any) {
      toast.error('Failed to move back', error?.data?.detail || 'Please try again');
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowConfirm(true)}
        disabled={disabled || isLoading}
        icon={<RotateCcw className="w-4 h-4" />}
        className="bg-yellow-600 hover:bg-yellow-700 text-white"
      >
        Move Back to Production
      </Button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              onClick={() => setShowConfirm(false)} 
            />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Move Job Back to Production?
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-3">
                      This will move job <strong className="font-bold text-gray-900">{jobNumber}</strong> back to the "New Orders" tab and reset all production progress.
                    </p>
                    
                    {/* Warning Box */}
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800 mb-1">
                            The following will be reset:
                          </h4>
                          <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                            <li>All workflow checkboxes (Glass Arrival, Sealing, Cutting, Spacer, Drawing)</li>
                            <li>All completion statuses</li>
                            <li>Rack and Temporary Rack values</li>
                            <li>Ordered & Checked status</li>
                            <li>Email locks (Review/Order emails)</li>
                          </ul>
                          <p className="text-xs text-red-700 mt-2 font-medium">
                            Production dates will be preserved.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <label htmlFor="move-back-notes" className="block text-sm font-medium text-gray-700">
                  Reason for moving back (Optional)
                </label>
                <textarea
                  id="move-back-notes"
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm p-3"
                  placeholder="Why is this job being moved back to production? (e.g., customer requested changes, quality issue, etc.)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">{notes.length}/500 characters</p>
              </div>
              
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center sm:ml-3 sm:w-auto bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  {isLoading ? 'Moving...' : 'Yes, Move Back to Production'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center sm:mt-0 sm:w-auto"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
